import { StatusData } from "@fairfleet/geotab";
import { TachographDeviceInfo, TachographFile, TachographManufacturer } from "../models/Tachograph";
import { getFileReferenceDate, getFileUploadDate } from "./tachographFiles";

/** The numbers the diagnostic "Tachograph manufacturer" reports. */
const MANUFACTURERS: Record<number, TachographManufacturer> = {
  0: "notAvailable",
  1: "unknown",
  2: "vdo",
  3: "stoneridge",
  4: "actia",
  5: "efas",
};

/** A calibration date outside this range is a misread value, not a date. */
const EARLIEST_CALIBRATION_MS = Date.UTC(2000, 0, 1);
const LATEST_CALIBRATION_MS = Date.UTC(2100, 0, 1);

export function toManufacturer(value: unknown): TachographManufacturer | null {
  return typeof value === "number" ? (MANUFACTURERS[value] ?? null) : null;
}

/** The tachograph reports the next calibration as seconds since 1970. */
export function toCalibrationDate(value: unknown): Date | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;

  const milliseconds = value * 1000;
  if (milliseconds < EARLIEST_CALIBRATION_MS || milliseconds > LATEST_CALIBRATION_MS) return null;

  return new Date(milliseconds);
}

type DiagnosticIds = {
  manufacturerId?: string;
  calibrationId?: string;
  remoteDownloadId?: string;
  downloadTaskId?: string;
  tachographStatusId?: string;
};

/** The newest reading of each diagnostic per vehicle. */
export function buildDeviceInfo(
  rows: StatusData[],
  { manufacturerId, calibrationId, remoteDownloadId, downloadTaskId, tachographStatusId }: DiagnosticIds,
): Map<string, TachographDeviceInfo> {
  const newest = new Map<string, { manufacturer?: number; calibration?: number; remoteDownload?: number;
    downloadTask?: number;
    tachographStatus?: number;
    times: Map<string, number>;
  }>();

  for (const row of rows) {
    const deviceId = row.device?.id;
    const diagnosticId = row.diagnostic?.id;
    const time = row.dateTime ? new Date(row.dateTime).getTime() : NaN;
    if (!deviceId || !diagnosticId || Number.isNaN(time) || typeof row.data !== "number") continue;

    const entry = newest.get(deviceId) ?? { times: new Map<string, number>() };
    const previousTime = entry.times.get(diagnosticId);
    if (previousTime !== undefined && previousTime >= time) continue;

    entry.times.set(diagnosticId, time);
    if (diagnosticId === manufacturerId) entry.manufacturer = row.data;
    if (diagnosticId === calibrationId) entry.calibration = row.data;
    if (diagnosticId === remoteDownloadId) entry.remoteDownload = row.data;
    if (diagnosticId === downloadTaskId) entry.downloadTask = row.data;
    if (diagnosticId === tachographStatusId) entry.tachographStatus = row.data;
    newest.set(deviceId, entry);
  }

  const info = new Map<string, TachographDeviceInfo>();
  for (const [deviceId, entry] of newest) {
    const lastReadingMs = Math.max(...entry.times.values());

    info.set(deviceId, {
      manufacturer: toManufacturer(entry.manufacturer),
      nextCalibration: toCalibrationDate(entry.calibration),
      ...(entry.remoteDownload === undefined ? {} : { remoteDownload: entry.remoteDownload === 1 }),
      ...(entry.downloadTask === undefined ? {} : { downloadTaskStatus: entry.downloadTask }),
      ...(entry.tachographStatus === undefined ? {} : { tachographStatus: entry.tachographStatus }),
      lastReading: Number.isFinite(lastReadingMs) ? new Date(lastReadingMs) : null,
    });
  }

  return info;
}

/** What the "Technical data" block of a vehicle unit download says about the tachograph. */
export type VuTechnicalData = {
  manufacturer: TachographManufacturer | null;
  /** The make as the file names it, for a make the diagnostic numbers do not cover. */
  manufacturerName: string;
  /** "4.1" for a VDO DTCO 4.1; empty where the software version does not tell. */
  generation: string;
  nextCalibration: Date | null;
};

/** The firms behind the makes. Continental builds the VDO tachographs, Intellic the Efas ones. */
const MANUFACTURER_NAMES: [RegExp, TachographManufacturer][] = [
  [/continental|vdo|siemens/i, "vdo"],
  [/stoneridge/i, "stoneridge"],
  [/actia/i, "actia"],
  [/efas|intellic/i, "efas"],
];

/**
 * Reads make, generation and next calibration from a vehicle file's summary — the
 * `vuManufacturer`, `vuSoftwareVersion` and `nextCalibrationDate` lines of its technical data
 * (TREP#35). Many tachographs never send the make as a reading, but every remote download
 * carries it.
 */
export function parseVuTechnicalData(summary: string | undefined): VuTechnicalData {
  const line = (key: string) => summary?.match(new RegExp(`${key}:?[ \t]+([^\r\n]+)`))?.[1].trim() ?? "";

  const manufacturerName = line("vuManufacturer");
  const manufacturer =
    MANUFACTURER_NAMES.find(([pattern]) => pattern.test(manufacturerName))?.[1] ?? null;

  // VDO counts the release in the first two digits: 4140 is a DTCO 4.1. Other makes number
  // their software differently, so the generation is left out there.
  const software = line("vuSoftwareVersion").match(/^(\d)(\d)\d{2}$/);
  const generation = manufacturer === "vdo" && software ? `${software[1]}.${software[2]}` : "";

  const calibration = line("nextCalibrationDate").match(/^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2})/);
  const calibrationMs = calibration
    ? Date.UTC(+calibration[1], +calibration[2] - 1, +calibration[3], +calibration[4], +calibration[5])
    : NaN;

  return {
    manufacturer,
    manufacturerName,
    generation,
    nextCalibration: Number.isNaN(calibrationMs) ? null : toCalibrationDate(calibrationMs / 1000),
  };
}

/** A make the reading actually names, as opposed to "not available" or "unknown". */
export function isKnownMake(manufacturer: TachographManufacturer | null | undefined): manufacturer is TachographManufacturer {
  return !!manufacturer && manufacturer !== "notAvailable" && manufacturer !== "unknown";
}

/**
 * Per vehicle, the technical data of the newest file that carries any. Not simply the newest
 * file: a download can leave the technical block out.
 */
export function latestTechnicalDataByDeviceId(files: TachographFile[]): Map<string, VuTechnicalData> {
  const newest = new Map<string, { time: number; data: VuTechnicalData }>();

  for (const file of files) {
    const deviceId = file.device?.id;
    if (!deviceId || file.archived === true || (typeof file.errors === "string" && file.errors.length > 0)) continue;

    const data = parseVuTechnicalData(file.summary);
    if (!data.manufacturerName && !data.nextCalibration) continue;

    const time = (getFileReferenceDate(file) ?? getFileUploadDate(file))?.getTime() ?? 0;
    const current = newest.get(deviceId);
    if (!current || time > current.time) newest.set(deviceId, { time, data });
  }

  return new Map([...newest].map(([deviceId, { data }]) => [deviceId, data]));
}
